// Budget Monitoring Service
// Handles budget threshold monitoring and alert generation

import { supabase } from './supabase';
import {
  BudgetAlert,
  BudgetThreshold,
  BudgetMonitoringResult,
  SyncConfiguration
} from '../../shared/types/metaSync';
import { DatabaseError } from '../../shared/utils/errorHandler';

export class BudgetMonitorService {
  
  // Monitor all campaign budgets and generate alerts
  async monitorAllBudgets(): Promise<BudgetMonitoringResult> {
    const result: BudgetMonitoringResult = {
      alerts_triggered: [],
      campaigns_checked: 0,
      budget_warnings: 0,
      budget_critical: 0,
      budget_depleted: 0,
      total_spend_tracked: 0
    };

    try {
      // Get all active campaigns with budget mappings
      const { data: campaigns, error: campaignsError } = await supabase
        .from('campaigns')
        .select(`
          id,
          name,
          campaign_budgets!inner(
            id,
            daily_amount,
            lifetime_amount,
            spent_amount,
            currency,
            status
          ),
          campaign_external_mappings!inner(
            external_campaign_id,
            platform
          )
        `)
        .eq('campaign_budgets.status', 'active')
        .eq('campaign_external_mappings.platform', 'meta');

      if (campaignsError) {
        throw new DatabaseError(`Failed to fetch campaigns: ${campaignsError.message}`);
      }

      if (!campaigns || campaigns.length === 0) {
        return result;
      }

      // Get sync configuration for alert thresholds
      const config = await this.getSyncConfiguration();
      const thresholds = config?.alert_thresholds || {
        warning: 75,
        critical: 90,
        danger: 95
      };

      // Monitor each campaign
      for (const campaign of campaigns) {
        try {
          const campaignResult = await this.monitorCampaignBudget(campaign, thresholds);
          
          result.campaigns_checked++;
          result.total_spend_tracked += campaignResult.current_spend;
          
          if (campaignResult.alerts.length > 0) {
            result.alerts_triggered.push(...campaignResult.alerts);
            
            // Count alert types
            campaignResult.alerts.forEach(alert => {
              switch (alert.alert_type) {
                case 'threshold':
                  result.budget_warnings++;
                  break;
                case 'overage':
                  result.budget_critical++;
                  break;
                case 'depletion':
                  result.budget_depleted++;
                  break;
              }
            });
          }
        } catch (error: any) {
          console.error(`Failed to monitor campaign ${campaign.id}:`, error);
        }
      }

      return result;

    } catch (error: any) {
      console.error('Budget monitoring failed:', error);
      throw error;
    }
  }

  // Monitor a specific campaign's budget
  async monitorCampaignBudget(
    campaign: any,
    thresholds: { warning: number; critical: number; danger: number }
  ): Promise<{ alerts: BudgetAlert[]; current_spend: number }> {
    const alerts: BudgetAlert[] = [];
    const budget = campaign.campaign_budgets[0];
    
    if (!budget) {
      return { alerts, current_spend: 0 };
    }

    const currentSpend = budget.spent_amount || 0;
    const dailyBudget = budget.daily_amount;
    const lifetimeBudget = budget.lifetime_amount;
    
    // Determine which budget to check against
    const activeBudget = lifetimeBudget || dailyBudget;
    if (!activeBudget || activeBudget <= 0) {
      return { alerts, current_spend: currentSpend };
    }

    const spendPercentage = (currentSpend / activeBudget) * 100;

    // Check thresholds and create alerts
    if (spendPercentage >= thresholds.danger) {
      const alert = await this.createBudgetAlert(
        campaign.id,
        'depletion',
        thresholds.danger,
        spendPercentage,
        activeBudget,
        currentSpend
      );
      if (alert) alerts.push(alert);
    } else if (spendPercentage >= thresholds.critical) {
      const alert = await this.createBudgetAlert(
        campaign.id,
        'overage',
        thresholds.critical,
        spendPercentage,
        activeBudget,
        currentSpend
      );
      if (alert) alerts.push(alert);
    } else if (spendPercentage >= thresholds.warning) {
      const alert = await this.createBudgetAlert(
        campaign.id,
        'threshold',
        thresholds.warning,
        spendPercentage,
        activeBudget,
        currentSpend
      );
      if (alert) alerts.push(alert);
    }

    return { alerts, current_spend: currentSpend };
  }

  // Create budget alert (avoiding duplicates)
  private async createBudgetAlert(
    campaignId: string,
    alertType: 'threshold' | 'overage' | 'depletion',
    thresholdPercentage: number,
    currentPercentage: number,
    budgetLimit: number,
    currentSpend: number
  ): Promise<BudgetAlert | null> {
    
    // Check if similar alert already exists recently (avoid spam)
    const recentThreshold = new Date(Date.now() - 3600000); // Last hour
    const { data: existingAlert } = await supabase
      .from('budget_alerts')
      .select('id')
      .eq('campaign_id', campaignId)
      .eq('alert_type', alertType)
      .gte('triggered_at', recentThreshold.toISOString())
      .single();

    if (existingAlert) {
      return null; // Don't create duplicate alert
    }

    // Create new alert
    const { data: newAlert, error } = await supabase
      .from('budget_alerts')
      .insert({
        campaign_id: campaignId,
        alert_type: alertType,
        threshold_percentage: thresholdPercentage,
        current_percentage: currentPercentage,
        budget_limit: budgetLimit,
        current_spend: currentSpend,
        acknowledged: false
      })
      .select('*')
      .single();

    if (error) {
      console.error(`Failed to create budget alert for campaign ${campaignId}:`, error);
      return null;
    }

    console.log(`Budget alert created: ${alertType} for campaign ${campaignId} at ${currentPercentage.toFixed(1)}%`);
    return newAlert as BudgetAlert;
  }

  // Get unacknowledged budget alerts
  async getUnacknowledgedAlerts(campaignId?: string): Promise<BudgetAlert[]> {
    let query = supabase
      .from('budget_alerts')
      .select(`
        *,
        campaigns(
          id,
          name,
          artists(
            id,
            name
          )
        )
      `)
      .eq('acknowledged', false)
      .order('triggered_at', { ascending: false });

    if (campaignId) {
      query = query.eq('campaign_id', campaignId);
    }

    const { data, error } = await query;

    if (error) {
      throw new DatabaseError(`Failed to get budget alerts: ${error.message}`);
    }

    return (data as any[]) || [];
  }

  // Acknowledge budget alert
  async acknowledgeBudgetAlert(alertId: string, userId: string): Promise<BudgetAlert> {
    const { data, error } = await supabase
      .from('budget_alerts')
      .update({
        acknowledged: true,
        acknowledged_by: userId,
        acknowledged_at: new Date().toISOString()
      })
      .eq('id', alertId)
      .select('*')
      .single();

    if (error) {
      throw new DatabaseError(`Failed to acknowledge budget alert: ${error.message}`);
    }

    return data as BudgetAlert;
  }

  // Get budget thresholds for a campaign
  async getBudgetThresholds(campaignId: string): Promise<BudgetThreshold | null> {
    const { data: campaign, error } = await supabase
      .from('campaigns')
      .select(`
        id,
        name,
        campaign_budgets!inner(
          daily_amount,
          lifetime_amount,
          spent_amount,
          currency
        )
      `)
      .eq('id', campaignId)
      .single();

    if (error || !campaign) {
      return null;
    }

    const budget = campaign.campaign_budgets[0];
    if (!budget) {
      return null;
    }

    const config = await this.getSyncConfiguration();
    const thresholds = config?.alert_thresholds || {
      warning: 75,
      critical: 90,
      danger: 95
    };

    const currentSpend = budget.spent_amount || 0;
    const activeBudget = budget.lifetime_amount || budget.daily_amount;

    // Calculate spend rate (rough estimate based on recent spend)
    const spendRatePerHour = await this.calculateSpendRate(campaignId);
    
    // Estimate time to budget depletion
    let estimatedDepletionTime: string | undefined;
    if (activeBudget && spendRatePerHour > 0) {
      const remainingBudget = activeBudget - currentSpend;
      const hoursRemaining = remainingBudget / spendRatePerHour;
      estimatedDepletionTime = new Date(Date.now() + (hoursRemaining * 3600000)).toISOString();
    }

    return {
      campaign_id: campaignId,
      warning_percentage: thresholds.warning,
      critical_percentage: thresholds.critical,
      danger_percentage: thresholds.danger,
      daily_budget: budget.daily_amount,
      lifetime_budget: budget.lifetime_amount,
      current_spend: currentSpend,
      spend_rate_per_hour: spendRatePerHour,
      estimated_depletion_time: estimatedDepletionTime
    };
  }

  // Calculate recent spend rate for a campaign
  private async calculateSpendRate(campaignId: string): Promise<number> {
    const last24Hours = new Date(Date.now() - 86400000); // 24 hours ago

    const { data: spendEvents, error } = await supabase
      .from('ad_spend_events')
      .select('amount, event_hour')
      .eq('campaign_id', campaignId)
      .gte('event_hour', last24Hours.toISOString())
      .order('event_hour', { ascending: false });

    if (error || !spendEvents || spendEvents.length === 0) {
      return 0;
    }

    // Calculate total spend and time range
    const totalSpend = spendEvents.reduce((sum: number, event: any) => sum + (event.amount || 0), 0);
    const hoursSpanned = Math.max(1, spendEvents.length); // At least 1 hour

    return totalSpend / hoursSpanned;
  }

  // Get sync configuration
  private async getSyncConfiguration(): Promise<SyncConfiguration | null> {
    const { data, error } = await supabase
      .from('sync_configurations')
      .select('*')
      .eq('source', 'meta')
      .single();

    if (error && error.code !== 'PGRST116') {
      throw new DatabaseError(`Failed to get sync configuration: ${error.message}`);
    }

    return data as SyncConfiguration | null;
  }

  // Get budget alert statistics
  async getBudgetAlertStats(campaignId?: string, days = 7): Promise<{
    total_alerts: number;
    warning_alerts: number;
    critical_alerts: number;
    depletion_alerts: number;
    acknowledged_alerts: number;
    unacknowledged_alerts: number;
    alert_rate_per_day: number;
  }> {
    const sinceDate = new Date(Date.now() - (days * 86400000));
    
    let query = supabase
      .from('budget_alerts')
      .select('alert_type, acknowledged')
      .gte('triggered_at', sinceDate.toISOString());

    if (campaignId) {
      query = query.eq('campaign_id', campaignId);
    }

    const { data: alerts, error } = await query;

    if (error) {
      throw new DatabaseError(`Failed to get budget alert stats: ${error.message}`);
    }

    const stats = {
      total_alerts: alerts?.length || 0,
      warning_alerts: 0,
      critical_alerts: 0,
      depletion_alerts: 0,
      acknowledged_alerts: 0,
      unacknowledged_alerts: 0,
      alert_rate_per_day: 0
    };

    if (alerts) {
      // Single iteration for better performance instead of 5 separate filter operations
      alerts.forEach((a: any) => {
        if (a.alert_type === 'threshold') stats.warning_alerts++;
        if (a.alert_type === 'overage') stats.critical_alerts++;
        if (a.alert_type === 'depletion') stats.depletion_alerts++;
        if (a.acknowledged) stats.acknowledged_alerts++;
        else stats.unacknowledged_alerts++;
      });
      stats.alert_rate_per_day = stats.total_alerts / days;
    }

    return stats;
  }

  // Bulk acknowledge alerts
  async bulkAcknowledgeAlerts(alertIds: string[], userId: string): Promise<number> {
    const { data, error } = await supabase
      .from('budget_alerts')
      .update({
        acknowledged: true,
        acknowledged_by: userId,
        acknowledged_at: new Date().toISOString()
      })
      .in('id', alertIds)
      .select('id');

    if (error) {
      throw new DatabaseError(`Failed to bulk acknowledge alerts: ${error.message}`);
    }

    return data?.length || 0;
  }
}

export const budgetMonitorService = new BudgetMonitorService();
